import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { Pill, fmtRelative } from '@/features/dossier/components/primitives'
import { captureThread, listPlatforms, listThreads, type Platform, type Thread } from './api'
import { ThreatFilter, useThreatBands } from './ThreatFilter'

const SEARCH_PLACEHOLDER = 'Search by handle, name or what was said…'
import { Metric, PlatformChip, SentimentChip, StatusChip, ThreatScore, Velocity } from './components'

/**
 * Every thread, and the way one gets in by hand.
 *
 * ## Capture is a first-class door, not a fallback
 *
 * Most of what a war room reacts to arrives as a screenshot in somebody's phone before any API
 * knows about it — a WhatsApp forward, a TikTok a field agent saw, a comment behind a login. A
 * room that could only act on what its integrations reached would sit idle through the
 * incidents that actually matter, so pasting one in is a button on the main list.
 */
export function ThreadsPage() {
  const navigate = useNavigate()
  const authorities = useSession((s) => s.authorities)
  const canTriage = hasPermission(authorities, 'warroom:triage')

  const [threads, setThreads] = useState<Thread[] | null>(null)
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [capturing, setCapturing] = useState(false)

  const [subjectId, setSubjectId] = useState('')
  const [platformCode, setPlatformCode] = useState('MANUAL')
  const [url, setUrl] = useState('')
  const [authorHandle, setAuthorHandle] = useState('')
  const [body, setBody] = useState('')
  const [query, setQuery] = useState('')
  const [draftQuery, setDraftQuery] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState({ pages: 1, elements: 0 })
  /**
   * Which dossier subject to look at.
   *
   * A war room watches several subjects at once and the feed mixes them, so "what is being
   * said about this candidate" was a question the screen could not answer — you read every
   * row and sorted them in your head. Narrowing to one subject is also what makes a judgement
   * across the whole set possible rather than a reaction to whichever thread is loudest.
   */
  const [filterSubject, setFilterSubject] = useState('')
  const [band, setBand] = useState('')
  const threatBands = useThreatBands()

  const load = useCallback(async () => {
    try {
      setError(null)
      const found = await listThreads({
        q: query || undefined,
        subjectId: filterSubject || undefined,
        band: band || undefined,
        page,
        size: 50,
      })
      setThreads(found.content)
      setTotal({ pages: found.totalPages, elements: found.totalElements })
    } catch (err) {
      setError(explain(err))
    }
  }, [query, page, filterSubject, band])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listPlatforms().then(setPlatforms).catch(() => setPlatforms([]))
    void listSubjects({ size: '100' }).then(setSubjects).catch(() => setSubjects([]))
  }, [])

  if (error && !threads) return <ErrorStrip message={error} />
  if (!threads) return <LoadingBlock label="Loading threads" />

  async function capture(event: React.FormEvent) {
    event.preventDefault()
    if (!subjectId || !body.trim()) return
    setBusy(true)
    setError(null)
    try {
      const saved = await captureThread({
        subjectId, platformCode, url: url.trim() || null,
        authorHandle: authorHandle.trim().replace(/^@/, '') || null,
        body: body.trim(),
      })
      setCapturing(false)
      setBody(''); setUrl(''); setAuthorHandle('')
      navigate(`/war-room/threads/${saved.id}`)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Threads"
        description="Everything the radar has found, plus everything somebody brought in by
                     hand. Ranked by how fast it is moving."
      />

      {error && <ErrorStrip message={error} />}

      {canTriage && (
        <Panel>
          <div className="flex flex-wrap items-center justify-between gap-2 p-2">
            <p className="text-[11px] text-muted-foreground">
              Seen something the radar has not? A screenshot usually arrives first.
            </p>
            <button type="button" onClick={() => setCapturing((v) => !v)}
                    className="rounded border border-border px-2 py-1 text-[11px]">
              {capturing ? 'Close' : 'Capture a thread'}
            </button>
          </div>

          {capturing && (
            <form onSubmit={capture} className="space-y-2 border-t border-border p-3">
              <div className="grid gap-2 sm:grid-cols-3">
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    About whom
                  </span>
                  <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                          required
                          className="h-7 rounded border border-input bg-background px-2
                                     text-[12px]">
                    <option value="">Choose a subject</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>{s.displayName}</option>
                    ))}
                  </select>
                  <span className="text-[10px] text-muted-foreground">
                    Their dossier is what any reply will cite.
                  </span>
                </label>
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Platform
                  </span>
                  <select value={platformCode} onChange={(e) => setPlatformCode(e.target.value)}
                          className="h-7 rounded border border-input bg-background px-2
                                     text-[12px]">
                    {platforms.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.label}{p.canPost ? '' : ' — watch only'}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Who posted it
                  </span>
                  <input value={authorHandle} onChange={(e) => setAuthorHandle(e.target.value)}
                         placeholder="@handle"
                         className="h-7 rounded border border-input bg-background px-2
                                    text-[12px]" />
                </label>
              </div>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Link (optional)
                </span>
                <input value={url} onChange={(e) => setUrl(e.target.value)}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  What it says
                </span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                          required
                          className="rounded border border-input bg-background px-2 py-1
                                     text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  Paste it as posted. Everything after this — the score, the tactic, the reply —
                  is a reading of these exact words.
                </span>
              </label>
              <button type="submit" disabled={busy || !subjectId || !body.trim()}
                      className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                 text-primary-foreground disabled:opacity-50">
                {busy ? 'Capturing…' : 'Capture it'}
              </button>
            </form>
          )}
        </Panel>
      )}

      {/*
        Search and paging sit above the table on the war room's four unbounded lists — threads,
        the approval queue, the ledger and coordination. A campaign accumulates all four for its
        whole life, and an operator hunting one is looking for a handle or a phrase they half
        remember. Scrolling is not a search.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <input value={draftQuery} onChange={(e) => setDraftQuery(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setQuery(draftQuery) } }}
               placeholder={SEARCH_PLACEHOLDER}
               className="h-7 w-full max-w-xs rounded border border-input bg-background px-2
                          text-[12px]" />
        <button type="button" onClick={() => { setPage(0); setQuery(draftQuery) }}
                className="rounded border border-border px-2 py-1 text-[11px]">Search</button>
        {query && (
          <button type="button"
                  onClick={() => { setDraftQuery(''); setQuery(''); setPage(0) }}
                  className="rounded border border-border px-2 py-1 text-[11px]">Clear</button>
        )}
        <select value={filterSubject}
                onChange={(e) => { setPage(0); setFilterSubject(e.target.value) }}
                className="h-7 rounded border border-input bg-background px-2 text-[12px]">
          <option value="">Every subject</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>{s.displayName}</option>
          ))}
        </select>
        <span className="text-[10px] text-muted-foreground">
          {total.elements} {total.elements === 1 ? 'thread' : 'threads'}
        </span>
      </div>
      {/*
        The threat filter gets its own row rather than another control in the line above. It
        narrows on a different axis from search and subject — those choose WHICH threads, this
        chooses how bad — and mixing them into one strip made it read as a third dropdown.
      */}
      <div className="mb-2">
        <ThreatFilter bands={threatBands} value={band}
                      onChange={(code) => { setPage(0); setBand(code) }} />
      </div>
      <Panel>
        <ErpTable head={<>
          {/*
            Four columns, not six. Platform and reach moved onto the line under the title —
            as columns of their own they squeezed the text that actually identifies a thread
            into a sliver, and they are attributes of the row rather than dimensions worth a
            column each. The header count and the cell count must move together; they did
            not, and the table rendered with two stray columns.
          */}
          <Th>Thread</Th>
          <Th>Threat</Th>
          <Th className="axis-hide-sm">Seen</Th>
          <Th>Status</Th>
        </>}>
          {threads.length === 0 && (
            <EmptyRow colSpan={4}
                      message="Nothing yet. Add a watch under Radar, or capture a thread by
                               hand." />
          )}
          {threads.map((t) => (
            <tr key={t.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/war-room/threads/${t.id}`)}>
              <Td>
                <span className="font-medium">{t.title ?? '(no text captured)'}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  {/*
                    The subject as a TAG, not a clause in a grey sentence. A room watches
                    several subjects at once and this feed mixes them, so "which candidate is
                    this about" is the first thing a reader needs and was the easiest thing to
                    miss when it was set in the same muted text as everything else.
                  */}
                  {/*
                    The tag is the way in to the subject view. stopPropagation because the row
                    itself opens the thread — clicking the subject means "show me everything
                    about him", not "open this one".
                  */}
                  {t.subjectId ? (
                    <button type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/war-room/subjects/${t.subjectId}`)
                            }}
                            className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2
                                       py-0.5 text-[10px] text-sky-700 hover:bg-sky-500/20
                                       dark:text-sky-400">
                      {t.subjectName ?? 'unnamed subject'}
                    </button>
                  ) : (
                    <Pill className="border-border">unnamed subject</Pill>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    {t.authorHandle ? `@${t.authorHandle}` : 'unknown author'}
                  </span>
                  {/*
                    Platform and reach on the SAME line as the handle, the way the situation
                    board already showed them. As separate columns they pushed the text that
                    actually identifies a thread into a sliver on anything narrower than a
                    desktop — and they are attributes of the row, not dimensions worth a column
                    each. Everything visible at once, and the title keeps the width.
                  */}
                  <PlatformChip code={t.platformCode} />
                  <Velocity value={t.velocity} />
                  <Metric label="likes" value={t.likes} />
                  <Metric label="replies" value={t.replies} />
                  <Metric label="reposts" value={t.reposts} />
                </span>
              </Td>
              <Td>
                <div className="flex items-center gap-1">
                  <ThreatScore score={t.threatScore} />
                  <SentimentChip code={t.sentimentCode} />
                </div>
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">
                  {fmtRelative(t.firstSeenAt)}
                </span>
              </Td>
              <Td><StatusChip code={t.statusCode} /></Td>
            </tr>
          ))}
        </ErpTable>

        <OffsetPager
          page={page}
          totalPages={total.pages}
          totalElements={total.elements}
          onPage={setPage}
          loading={false}
          unit="threads"
        />
      </Panel>
    </div>
  )
}
